/* ==========================================================================
   BRYVO: theme.js
   Site-wide behaviour, with no dependencies:
     1. Scroll reveals and off-screen animation pausing (IntersectionObserver)
     2. Floating header: hide on scroll down, show on scroll up, mobile menu
     3. Pointer effects: cursor light, card edge lighting, tilt, magnetic buttons, parallax
     4. Section jump navigation (scroll spy)
     5. Shopify theme editor support
   Everything is progressive enhancement: the page is complete and usable without it.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var motion = root.getAttribute('data-motion') || 'full';
  var reduce = motion === 'off';
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var finePointer = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  var pointerFx = motion === 'full' && finePointer;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function revealAll(scope) {
    each((scope || doc).querySelectorAll('.reveal, [data-inview]'), function (node) {
      node.classList.add('is-inview');
    });
  }

  /* Run a feature in isolation: one failure must never hide content or break the others. */
  function safely(name, fn) {
    try {
      fn();
    } catch (error) {
      revealAll();
      if (window.console && console.warn) console.warn('[BRYVO] ' + name + ' was skipped: ' + (error && error.message));
    }
  }

  /* ---------- 1. Reveals ---------- */
  var revealObserver = null;
  var animObserver = null;

  function observeReveals(scope) {
    var nodes = (scope || doc).querySelectorAll('.reveal, [data-inview]');
    if (reduce || designMode || !('IntersectionObserver' in window)) {
      revealAll(scope);
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-inview');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -7% 0px', threshold: 0.01 }
      );
    }
    each(nodes, function (node) {
      if (!node.classList.contains('is-inview')) revealObserver.observe(node);
    });
  }

  /* Looping decorative animations only run while they are on screen. */
  function observeAnimations(scope) {
    if (reduce || !('IntersectionObserver' in window)) return;
    if (!animObserver) {
      animObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            entry.target.classList.toggle('is-offscreen', !entry.isIntersecting);
          });
        },
        { rootMargin: '80px 0px 80px 0px' }
      );
    }
    each((scope || doc).querySelectorAll('[data-anim]'), function (node) {
      animObserver.observe(node);
    });
  }

  /* ---------- 2. Header ---------- */
  var lastY = Math.max(0, window.pageYOffset || 0);
  var scrollTicking = false;
  var SHOW_ABOVE = 140; /* always visible near the top of the page */
  var DOWN_THRESHOLD = 10; /* px of movement before hiding */
  var UP_THRESHOLD = 6; /* px of movement before showing */

  function getHeader() {
    return doc.querySelector('[data-header]');
  }

  function menuIsOpen(header) {
    return !!header && header.classList.contains('is-open');
  }

  function setHidden(header, hidden) {
    if (!header) return;
    header.classList.toggle('is-hidden', hidden);
    root.classList.toggle('header-hidden', hidden);
  }

  function setMenu(open, returnFocus) {
    var header = getHeader();
    if (!header) return;
    var toggle = header.querySelector('[data-menu-toggle]');
    var menu = header.querySelector('[data-menu]');
    if (!toggle || !menu) return;
    header.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    var label = toggle.querySelector('[data-menu-label]');
    if (label) label.textContent = toggle.getAttribute(open ? 'data-label-close' : 'data-label-open') || label.textContent;
    if (open) {
      menu.removeAttribute('inert');
      setHidden(header, false);
      var first = menu.querySelector('a, button');
      if (first) window.setTimeout(function () { first.focus({ preventScroll: true }); }, 60);
    } else {
      menu.setAttribute('inert', '');
      if (returnFocus) toggle.focus({ preventScroll: true });
    }
  }

  function prepareMenu() {
    var header = getHeader();
    if (!header) return;
    var menu = header.querySelector('[data-menu]');
    if (menu && !menuIsOpen(header)) menu.setAttribute('inert', '');
    header.classList.toggle('is-scrolled', lastY > 24);
  }

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(function () {
      scrollTicking = false;
      var header = getHeader();
      var y = Math.max(0, window.pageYOffset || 0);
      var delta = y - lastY;
      if (!header) {
        lastY = y;
        return;
      }
      header.classList.toggle('is-scrolled', y > 24);
      if (reduce || menuIsOpen(header) || header.contains(doc.activeElement)) {
        setHidden(header, false);
        lastY = y;
        return;
      }
      if (y < SHOW_ABOVE) {
        setHidden(header, false);
        lastY = y;
      } else if (delta > DOWN_THRESHOLD) {
        setHidden(header, true);
        lastY = y;
      } else if (delta < -UP_THRESHOLD) {
        setHidden(header, false);
        lastY = y;
      }
    });
  }

  function initHeader() {
    prepareMenu();
    window.addEventListener('scroll', onScroll, { passive: true });

    doc.addEventListener('click', function (event) {
      var header = getHeader();
      if (!header) return;
      if (event.target.closest('[data-menu-toggle]')) {
        setMenu(!menuIsOpen(header), false);
        return;
      }
      if (!menuIsOpen(header)) return;
      if (event.target.closest('[data-menu] a') || !event.target.closest('[data-header]')) setMenu(false, false);
    });

    doc.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && menuIsOpen(getHeader())) setMenu(false, true);
    });

    /* Keyboard users: reveal the header when focus enters it, close the menu when focus leaves it. */
    doc.addEventListener('focusin', function (event) {
      var header = getHeader();
      if (!header) return;
      if (header.contains(event.target)) setHidden(header, false);
      else if (menuIsOpen(header)) setMenu(false, false);
    });

    if (window.matchMedia) {
      var desktop = window.matchMedia('(min-width: 62rem)');
      var onChange = function (mq) {
        if (mq.matches && menuIsOpen(getHeader())) setMenu(false, false);
      };
      if (desktop.addEventListener) desktop.addEventListener('change', onChange);
      else if (desktop.addListener) desktop.addListener(onChange);
    }
  }

  /* ---------- 3. Pointer effects ---------- */
  function initPointer() {
    if (!pointerFx) return;
    var light = doc.querySelector('.cursor-light');
    var x = 0;
    var y = 0;
    var target = null;
    var frame = 0;
    var activeSpot = null;
    var activeMagnet = null;
    var activeParallax = null;

    function resetSpot(el) {
      if (!el) return;
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    }

    function resetMagnet(el) {
      if (el) el.style.translate = '';
    }

    function resetParallax(el) {
      if (!el) return;
      el.style.setProperty('--px', '0');
      el.style.setProperty('--py', '0');
    }

    function update() {
      frame = 0;
      var el = target && target.closest ? target : null;

      if (light) {
        light.style.setProperty('--cx', x + 'px');
        light.style.setProperty('--cy', y + 'px');
        light.classList.add('is-active');
      }

      /* Card edge lighting and tilt */
      var spot = el ? el.closest('[data-spot]') : null;
      if (spot) {
        var rect = spot.getBoundingClientRect();
        spot.style.setProperty('--mx', (x - rect.left).toFixed(1) + 'px');
        spot.style.setProperty('--my', (y - rect.top).toFixed(1) + 'px');
        if (spot.hasAttribute('data-tilt') && rect.width && rect.height) {
          var max = parseFloat(spot.getAttribute('data-tilt')) || 3.5;
          var nx = (x - rect.left) / rect.width - 0.5;
          var ny = (y - rect.top) / rect.height - 0.5;
          spot.classList.add('is-tilt');
          spot.style.setProperty('--ry', (nx * max * 2).toFixed(2) + 'deg');
          spot.style.setProperty('--rx', (-ny * max * 2).toFixed(2) + 'deg');
        }
      }
      if (activeSpot && activeSpot !== spot) resetSpot(activeSpot);
      activeSpot = spot;

      /* Magnetic buttons */
      var magnet = el ? el.closest('[data-magnetic]') : null;
      if (magnet) {
        var m = magnet.getBoundingClientRect();
        var dx = x - (m.left + m.width / 2);
        var dy = y - (m.top + m.height / 2);
        magnet.style.translate = (dx * 0.16).toFixed(1) + 'px ' + (dy * 0.28).toFixed(1) + 'px';
        magnet.style.setProperty('--bx', (((x - m.left) / m.width) * 100).toFixed(0) + '%');
        magnet.style.setProperty('--by', (((y - m.top) / m.height) * 100).toFixed(0) + '%');
      }
      if (activeMagnet && activeMagnet !== magnet) resetMagnet(activeMagnet);
      activeMagnet = magnet;

      /* Layered parallax inside marked areas */
      var parallax = el ? el.closest('[data-parallax]') : null;
      if (parallax) {
        var p = parallax.getBoundingClientRect();
        parallax.style.setProperty('--px', (((x - p.left) / p.width - 0.5) * 2).toFixed(3));
        parallax.style.setProperty('--py', (((y - p.top) / p.height - 0.5) * 2).toFixed(3));
      }
      if (activeParallax && activeParallax !== parallax) resetParallax(activeParallax);
      activeParallax = parallax;
    }

    doc.addEventListener(
      'pointermove',
      function (event) {
        if (event.pointerType === 'touch') return;
        x = event.clientX;
        y = event.clientY;
        target = event.target;
        if (!frame) frame = window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    root.addEventListener('mouseleave', function () {
      if (light) light.classList.remove('is-active');
      resetSpot(activeSpot);
      resetMagnet(activeMagnet);
      resetParallax(activeParallax);
      activeSpot = activeMagnet = activeParallax = null;
    });
  }

  /* ---------- 4. Jump navigation (scroll spy) ---------- */
  var spyObserver = null;

  function initSpy(scope) {
    var navs = (scope || doc).querySelectorAll('[data-spy]');
    if (!navs.length || !('IntersectionObserver' in window)) return;
    each(navs, function (nav) {
      if (nav.getAttribute('data-spy') === 'ready') return;
      nav.setAttribute('data-spy', 'ready');
      var links = nav.querySelectorAll('a[href^="#"]');
      var byId = {};
      each(links, function (link) {
        var id = decodeURIComponent(link.getAttribute('href').slice(1));
        if (id) byId[id] = link;
      });
      spyObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var current = byId[entry.target.id];
            if (!current) return;
            each(links, function (link) {
              if (link === current) link.setAttribute('aria-current', 'true');
              else link.removeAttribute('aria-current');
            });
            var scroller = nav.querySelector('[data-spy-scroller]');
            if (scroller && scroller.scrollWidth > scroller.clientWidth) {
              var left = current.offsetLeft - (scroller.clientWidth - current.offsetWidth) / 2;
              scroller.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' });
            }
          });
        },
        { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
      );
      Object.keys(byId).forEach(function (id) {
        var section = doc.getElementById(id);
        if (section) spyObserver.observe(section);
      });
    });
  }

  /* ---------- 5. Shopify theme editor ---------- */
  function initEditor() {
    doc.addEventListener('shopify:section:load', function (event) {
      safely('section load', function () {
        prepareMenu();
        observeReveals(event.target);
        observeAnimations(event.target);
        initSpy(event.target);
      });
    });
    doc.addEventListener('shopify:block:select', function (event) {
      var details = event.target.closest ? event.target.closest('details') : null;
      if (details) details.open = true;
    });
  }

  function init() {
    safely('reveals', function () { observeReveals(doc); });
    safely('animations', function () { observeAnimations(doc); });
    safely('header', initHeader);
    safely('pointer effects', initPointer);
    safely('jump navigation', function () { initSpy(doc); });
    safely('theme editor', initEditor);
  }

  function markReady() {
    window.setTimeout(function () { root.classList.add('is-ready'); }, 300);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  if (doc.readyState === 'complete') markReady();
  else window.addEventListener('load', markReady, { once: true });
})();
